import { useEffect } from "react";

import { KeyBy } from "@m7-health/shared-utils";
import { entries, groupBy, keyBy } from "lodash";

import { useAppConfigQuery } from "#/features/User/queries";
import { IStaffCategory, IStaffType } from "@/api";
import { useDeepMemo } from "@/common/hooks";

import { TAppConfigState, useAppConfigDispatch } from "../../store";

import type { PayloadAction } from "@reduxjs/toolkit";

export type IStaffCategoryState = {
  staffCategories: IStaffCategory[];
  staffCategoryByStaffTypeKey: Partial<Record<IStaffType["key"], IStaffCategory>>;
  staffCategoriesByKey: Partial<KeyBy<IStaffCategory, "key">>;
};

export const staffCategoryInitialState: IStaffCategoryState = {
  staffCategories: [],
  staffCategoryByStaffTypeKey: {},
  staffCategoriesByKey: {},
};

export const staffCategoryActions = {
  setStaffCategories: (
    { staffCategory: state }: TAppConfigState,
    { payload }: PayloadAction<{ staffCategories: IStaffCategory[] }>,
  ) => {
    state.staffCategories = payload.staffCategories;
    state.staffCategoriesByKey = keyBy(payload.staffCategories, "key");
  },

  setStaffCategoryByStaffTypeKey: (
    { staffCategory: state }: TAppConfigState,
    { payload }: PayloadAction<{ staffTypes: IStaffType[]; staffCategories: IStaffCategory[] }>,
  ) => {
    const { staffTypes, staffCategories } = payload;
    const staffCategoriesByKey = keyBy(staffCategories, "key");

    const staffTypesByStaffCategoryKey = groupBy(staffTypes, "staffCategoryKey");
    state.staffCategoryByStaffTypeKey = entries(staffTypesByStaffCategoryKey).reduce(
      (acc, [staffTypeKey, staffCategoryTypes]) => {
        const staffCategory = staffCategoriesByKey[staffTypeKey];
        if (!staffCategory) return acc;

        staffCategoryTypes.forEach((staffType) => (acc[staffType.key] = staffCategory));

        return acc;
      },
      {} as Partial<Record<IStaffType["key"], IStaffCategory>>,
    );
  },
};

export const useSynchStaffCategories = () => {
  const dispatch = useAppConfigDispatch();
  const appConfig = useAppConfigQuery();

  const staffCategories = useDeepMemo(
    () => appConfig.data?.staffCategories,
    [appConfig.data?.staffCategories],
  );
  const staffTypes = useDeepMemo(() => appConfig.data?.staffTypes, [appConfig.data?.staffTypes]);

  useEffect(() => {
    if (!staffCategories?.length || !staffTypes?.length) return;

    dispatch.setStaffCategories({ staffCategories });
    dispatch.setStaffCategoryByStaffTypeKey({ staffCategories, staffTypes });
  }, [staffCategories, staffTypes, dispatch]);
};
